import React, { useState, useRef, useEffect } from 'react';
import { Share2, Download, Loader2, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Renders a beautifully designed share card for the user's quiz score
 * and provides Share / Download / WhatsApp actions.
 *
 * Props:
 *  - score, total, percentage, time_taken (numbers)
 *  - userName (string)
 *  - subject (string, optional — e.g. "Physics", "JEE Mock")
 *  - referralCode (string)
 *  - inviteUrl (string, e.g. "https://aptivon.in/register?ref=APT1234")
 */
const ScoreShareCard = ({ score, total, percentage, time_taken, userName, subject, referralCode, inviteUrl }) => {
  const canvasRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);

  const heroColor = percentage >= 80 ? '#10B981' : percentage >= 50 ? '#F59E0B' : '#EF4444';
  const accent = percentage >= 80 ? '#34D399' : percentage >= 50 ? '#FBBF24' : '#F87171';
  const emoji = percentage >= 80 ? '🏆' : percentage >= 50 ? '🔥' : '💪';
  const tagline = percentage >= 80 ? 'Crushing it!' : percentage >= 50 ? 'Solid run' : 'Bouncing back';

  // Build the canvas once on mount / whenever score changes.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const W = 1080;
    const H = 1080;
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');

    // Background gradient
    const bg = ctx.createLinearGradient(0, 0, W, H);
    bg.addColorStop(0, '#0F172A');
    bg.addColorStop(1, '#1E293B');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    // Top accent strip
    const strip = ctx.createLinearGradient(0, 0, W, 0);
    strip.addColorStop(0, heroColor);
    strip.addColorStop(1, accent);
    ctx.fillStyle = strip;
    ctx.fillRect(0, 0, W, 14);

    // Branding
    ctx.fillStyle = '#7DD3FC';
    ctx.font = 'bold 38px Inter, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('APTIVON', 64, 100);
    ctx.fillStyle = '#94A3B8';
    ctx.font = '24px Inter, sans-serif';
    ctx.fillText('JEE / NEET Quiz Battles', 64, 140);

    // Emoji + tagline
    ctx.textAlign = 'right';
    ctx.font = '120px Inter, sans-serif';
    ctx.fillText(emoji, W - 64, 145);

    // Main score block
    ctx.textAlign = 'center';
    ctx.fillStyle = '#F8FAFC';
    ctx.font = '34px Inter, sans-serif';
    ctx.fillText((userName || 'A scholar').slice(0, 28), W / 2, 280);

    ctx.fillStyle = '#CBD5E1';
    ctx.font = '28px Inter, sans-serif';
    ctx.fillText(subject ? `${subject} Quiz` : 'Quiz Result', W / 2, 325);

    // Score number — gigantic
    ctx.fillStyle = heroColor;
    ctx.font = 'bold 280px Inter, sans-serif';
    ctx.fillText(`${percentage}%`, W / 2, 620);

    ctx.fillStyle = '#F8FAFC';
    ctx.font = 'bold 60px Inter, sans-serif';
    ctx.fillText(`${score} / ${total} correct`, W / 2, 720);

    ctx.fillStyle = accent;
    ctx.font = 'bold 36px Inter, sans-serif';
    ctx.fillText(`⚡ ${tagline}  ·  ⏱ ${time_taken}s`, W / 2, 790);

    // Divider
    ctx.strokeStyle = '#334155';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(120, 870);
    ctx.lineTo(W - 120, 870);
    ctx.stroke();

    // CTA box
    ctx.fillStyle = '#1E293B';
    ctx.fillRect(64, 905, W - 128, 130);
    ctx.strokeStyle = heroColor;
    ctx.lineWidth = 3;
    ctx.strokeRect(64, 905, W - 128, 130);

    ctx.fillStyle = '#F8FAFC';
    ctx.font = 'bold 36px Inter, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Beat my score on Aptivon', 100, 960);

    ctx.fillStyle = accent;
    ctx.font = 'bold 30px Inter, sans-serif';
    ctx.fillText(referralCode ? `Use code ${referralCode} → +50 pts free` : 'aptivon.in', 100, 1005);

    // URL bottom-right
    ctx.fillStyle = '#7DD3FC';
    ctx.font = '24px Inter, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText('aptivon.in', W - 100, 990);

    // Convert to dataURL for <img> preview / fallback
    try {
      const url = canvas.toDataURL('image/png');
      setImageUrl(url);
    } catch {
      setImageUrl(null);
    }
  }, [score, total, percentage, time_taken, userName, subject, referralCode, heroColor, accent, emoji, tagline]);

  const dataUrlToBlob = (dataUrl) => {
    const [header, base64] = dataUrl.split(',');
    const mime = header.match(/data:(.*?);/)[1];
    const bin = atob(base64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return new Blob([arr], { type: mime });
  };

  const captionText = (() => {
    const base = `I scored ${score}/${total} (${percentage}%) on Aptivon's ${subject || 'JEE/NEET'} quiz ${emoji}`;
    const cta = referralCode
      ? `\nUse my code ${referralCode} to grab +50 pts free → ${inviteUrl || 'https://aptivon.in'}`
      : `\nPlay free at https://aptivon.in`;
    return base + cta;
  })();

  const handleDownload = () => {
    if (!imageUrl) return;
    const a = document.createElement('a');
    a.href = imageUrl;
    a.download = `aptivon-score-${percentage}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success('Saved! Now post it 🔥');
  };

  const handleNativeShare = async () => {
    if (!imageUrl) return;
    setBusy(true);
    try {
      const blob = dataUrlToBlob(imageUrl);
      const file = new File([blob], 'aptivon-score.png', { type: 'image/png' });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: captionText, title: 'My Aptivon score' });
      } else if (navigator.share) {
        await navigator.share({ text: captionText, title: 'My Aptivon score', url: inviteUrl });
      } else {
        await navigator.clipboard.writeText(captionText);
        handleDownload();
        toast.success('Caption copied & image saved — post on Insta/WhatsApp!');
      }
    } catch (e) {
      // User cancelled — silent
    } finally {
      setBusy(false);
    }
  };

  const handleWhatsApp = () => {
    handleDownload();
    setTimeout(() => {
      const wa = `https://wa.me/?text=${encodeURIComponent(captionText)}`;
      window.open(wa, '_blank', 'noopener,noreferrer');
    }, 200);
  };

  return (
    <div className="rounded-3xl border-2 border-[#0EA5E9]/30 bg-gradient-to-br from-[#0F172A] to-[#1E293B] p-5 sm:p-6 text-white shadow-xl" data-testid="score-share-card">
      <div className="flex items-center gap-2 mb-3">
        <Share2 className="w-5 h-5 text-amber-300" />
        <p className="text-xs font-bold uppercase tracking-wider text-amber-300">Brag a little</p>
      </div>
      <h3 className="text-xl sm:text-2xl font-black mb-1" style={{ fontFamily: 'Fredoka, sans-serif' }}>
        Share your score, earn referrals
      </h3>
      <p className="text-sm text-slate-300 mb-4">
        Auto-generated Insta-ready card with your invite code baked in.
      </p>

      <canvas ref={canvasRef} className="hidden" />
      {imageUrl && (
        <div className="rounded-2xl overflow-hidden mb-4 bg-black/40">
          <img src={imageUrl} alt="Score share card preview" className="w-full h-auto" data-testid="share-card-preview" />
        </div>
      )}

      <div className="grid grid-cols-3 gap-2">
        <button
          onClick={handleWhatsApp}
          disabled={!imageUrl || busy}
          className="px-3 py-2.5 rounded-xl font-bold bg-[#25D366] text-white text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
          data-testid="share-card-whatsapp"
        >
          <MessageCircle className="w-4 h-4" /> WhatsApp
        </button>
        <button
          onClick={handleNativeShare}
          disabled={!imageUrl || busy}
          className="px-3 py-2.5 rounded-xl font-bold bg-amber-400 text-[#111827] text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
          data-testid="share-card-share"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} Share
        </button>
        <button
          onClick={handleDownload}
          disabled={!imageUrl}
          className="px-3 py-2.5 rounded-xl font-bold bg-white/15 text-white text-sm inline-flex items-center justify-center gap-1.5 hover:bg-white/25 disabled:opacity-50"
          data-testid="share-card-download"
        >
          <Download className="w-4 h-4" /> Save
        </button>
      </div>
    </div>
  );
};

export default ScoreShareCard;
